/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/Annotations/Common" />
import * as React from "react";
import { getTFSProject } from "Tfs/Platform/Page";
import { useObservable } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext } from "VSS/Platform/Layout";
import { BoltListDragEvent } from "VSSUI/List";
import { TextField } from "VSSUI/TextField";
import { IChecklistItem, IItemAnnotations, IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { WorkItemTypeColorAndIconMap } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { FocusOnMount } from "Wit/Common/Controls/Boards/FocusOnMount";
import { CoreFieldRefNames, DalFields } from "Wit/Common/Generated/Constants";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { IIdentity } from "VSSUI/IdentityPicker";

export interface IAnnotationComponentProps {
    annotations: IItemAnnotations | undefined;
    expanded: boolean;
    item: IObservableBoardsWorkItem;
    name: string;
    onDrag?: (event: PointerEvent, dragEvent: BoltListDragEvent<HTMLElement, IChecklistItem>) => void;
    onClick: (name: string) => void;
    workItemTypeColorAndIconsMap: WorkItemTypeColorAndIconMap;
    itemsColor?: string;
    personIdentities: IIdentity[] | undefined;
    onModifyItems?: () => void;
}

/**
 * Each annotation component should conform to this type
 */
export type AnnotationComponent = React.FunctionComponent<IAnnotationComponentProps>;

interface IAnnotationItemTextboxPropsCommon {
    onComplete: (createMoreItem?: boolean) => void;
    parent: IObservableBoardsWorkItem;
}

/**
 * Props only used when adding an item
 */
interface IAddAnnotationItemTextboxProps extends IAnnotationItemTextboxPropsCommon {
    linkType: Promise<number> | number;
    onAdd: (newItem: IWorkItem) => void;
    type: Promise<string> | string;

    onEdit?: never;
    title?: never;
    workItemId?: never;
}

/**
 * Props only used when editing an item
 */
interface IEditAnnotationItemTextboxProps extends IAnnotationItemTextboxPropsCommon {
    onEdit: (newTitle: string) => void;
    title: string;
    workItemId: number;

    linkType?: never;
    onAdd?: never;
    type?: never;
}

type IAnnotationItemTextboxProps = IAddAnnotationItemTextboxProps | IEditAnnotationItemTextboxProps;

export function AnnotationItemTextbox({ linkType, onAdd, onComplete, onEdit, parent, title, type, workItemId }: IAnnotationItemTextboxProps) {
    const pageContext = usePageContext();
    const [saving, setSaving] = React.useState(false);
    const [titleObservable] = useObservable(title ?? "");

    const eventHandler = React.useCallback(async (e?: React.KeyboardEvent) => {
        if (e?.defaultPrevented) {
            return;
        }
        let createExtraItem: boolean = false;
        if (!e || e.key === "Enter") {
            // e will be undefined on blur events
            if (titleObservable.value) {
                try {
                    setSaving(true);
                    if (onAdd && type && linkType) {
                        createExtraItem = e?.key === "Enter";
                        const newItem = await createNewChildItem(pageContext, parent, await type, titleObservable.value, await linkType);

                        if (newItem.isValid()) {
                            await newItem.save();

                            // update parent's annotations
                            onAdd(newItem);
                        } else {
                            const dialogService = pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
                            dialogService.showWorkItemFormDialog(newItem, () => {
                                onAdd(newItem);
                            });
                        }
                    } else if (onEdit && workItemId && title !== titleObservable.value) {
                        const newTitle = titleObservable.value;
                        await updateWorkItemTitle(pageContext, newTitle, workItemId);
                        onEdit(newTitle);
                    }

                    parent.notify();
                } finally {
                    setSaving(false);
                }
            }
            onComplete(createExtraItem);
        } else if (e.key === "Escape") {
            onComplete();
        }
    }, []);
    return (
        <FocusOnMount>
            <TextField
                ariaLabel={Resources.EnterTitle}
                className="margin-top-4"
                containerClassName="flex-grow"
                disabled={saving}
                inputClassName="add-item-text-field body-s"
                onBlur={eventHandler}
                onChange={(e, newValue) => (titleObservable.value = newValue)}
                onKeyDown={eventHandler}
                value={titleObservable}
            />
        </FocusOnMount>
    );
}

async function createNewChildItem(pageContext: IVssPageContext, parent: IObservableBoardsWorkItem, type: string, title: string, linkType: number) {
    const project = getTFSProject(pageContext);
    const teamSettingsService = pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
    const teamSettings = await teamSettingsService.getTeamSettings();
    const workItemModelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
    const typeData = await workItemModelService.getWorkItemTypeData(project!.id, [type]);
    if (!typeData) {
        throw Error(Resources.ErrorFetchingWorkItemTypeData);
    }
    const newItem = await workItemModelService.createWorkItem(typeData[0], project!.id);
    newItem.setFieldValueByName(CoreFieldRefNames.Title, title);
    newItem.addLinks([
        new WorkItemLink({
            Comment: "",
            FldID: DalFields.RelatedLinks,
            ID: parent.value.id,
            LinkType: linkType,
            isAddedBySystem: true
        })
    ]);
    newItem.setFieldValueByName(CoreFieldRefNames.IterationPath, parent.value.getFieldValue(CoreFieldRefNames.IterationPath));
    newItem.setFieldValueByName(CoreFieldRefNames.AreaPath, parent.value.getFieldValue(CoreFieldRefNames.AreaPath));
    if (teamSettings?.teamFieldName) {
        newItem.setFieldValueByName(teamSettings.teamFieldName, parent.value.getFieldValue(teamSettings.teamFieldName));
    }
    return newItem;
}

async function updateWorkItemTitle(pageContext: IVssPageContext, title: string, id: number) {
    const workItemModelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
    const item = await workItemModelService.getWorkItem(id);
    item.setFieldValueByName(CoreFieldRefNames.Title, title);
    await item.save();
}
